import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Loader2, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { useT } from "@/lib/i18n";

const toDate = (iso) => new Date(`${iso}T00:00:00`);

export const toIsoDate = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const nightsBetween = (from, to) =>
  Math.round((to.getTime() - from.getTime()) / 86400000);

/**
 * Stay-date picker backed by live availability from GET /api/availability.
 *
 * Blocked nights are unselectable, including as a check-out day: for a single
 * cottage the changeover day is needed for cleaning, so over-blocking by one
 * day is intentional and safer than risking a double booking.
 */
export const DateRangeField = ({ value, onChange }) => {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/availability")
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("failed"))))
      .then((data) => {
        if (cancelled) return;
        setBlocked((data.blocked ?? []).map((b) => ({ from: toDate(b.start), to: toDate(b.end) })));
      })
      // On failure the picker still works; the server re-checks on submit.
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const disabled = useMemo(() => [{ before: today }, ...blocked], [today, blocked]);

  const nights = value?.from && value?.to ? nightsBetween(value.from, value.to) : 0;

  const nightsLabel = (n) => {
    if (lang === "en") return `${n} ${n === 1 ? "night" : "nights"}`;
    if (n === 1) return "1 noc";
    if (n < 5) return `${n} noci`;
    return `${n} nocí`;
  };

  const fmt = (date) =>
    date.toLocaleDateString(lang === "en" ? "en-GB" : "sk-SK", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  const summary = () => {
    if (value?.from && value?.to) return `${fmt(value.from)} → ${fmt(value.to)} · ${nightsLabel(nights)}`;
    if (value?.from) return `${fmt(value.from)} → ${t.contact.dates.pickCheckout}`;
    return t.contact.dates.placeholder;
  };

  return (
    <div className="sm:col-span-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{t.contact.dates.label}</span>
        {value?.from && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            data-testid="dates-clear"
          >
            <X className="h-3 w-3" />
            {t.contact.dates.clear}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="mt-1.5 flex h-11 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-left text-sm shadow-sm hover:bg-secondary/40 focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
        data-testid="dates-trigger"
      >
        <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className={value?.from ? "text-foreground" : "text-muted-foreground"}>{summary()}</span>
        {loading && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-border bg-card p-1 shadow-lifted" data-testid="dates-calendar">
          <Calendar
            mode="range"
            selected={value}
            onSelect={(range) => {
              onChange(range);
              if (range?.from && range?.to) setOpen(false);
            }}
            disabled={disabled}
            modifiers={{ booked: blocked }}
            modifiersClassNames={{ booked: "line-through opacity-60" }}
            excludeDisabled
            numberOfMonths={1}
            defaultMonth={value?.from ?? today}
            fromDate={today}
            className="sm:hidden"
          />
          <Calendar
            mode="range"
            selected={value}
            onSelect={(range) => {
              onChange(range);
              if (range?.from && range?.to) setOpen(false);
            }}
            disabled={disabled}
            modifiers={{ booked: blocked }}
            modifiersClassNames={{ booked: "line-through opacity-60" }}
            excludeDisabled
            numberOfMonths={2}
            defaultMonth={value?.from ?? today}
            fromDate={today}
            className="hidden sm:block"
          />
          <p className="px-3 pb-2 text-xs text-muted-foreground">{t.contact.dates.legend}</p>
        </div>
      )}
    </div>
  );
};
